import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import BrandSymbolSelector from '../shared/BrandSymbolSelector';
import GentlingChecklist from '../shared/GentlingChecklist';
import { DEFAULT_HORSE_FORM } from '../../../constants/horseConstants';
import { resolveImageUrl } from '../../../utils/horseHelpers';
import { processImageForUpload, isValidImageType, formatFileSize, ACCEPTED_IMAGE_TYPES } from '../../../utils/imageUtils';
import { toast } from 'sonner';

/**
 * Add/Edit Horse Dialog Component
 * Modal for adding or editing horse information
 */
const AddEditHorseDialog = ({ open, horse, onClose, onSave, api }) => {
  const [formData, setFormData] = useState(() => ({ ...DEFAULT_HORSE_FORM }));
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize form data when dialog opens
  const initializeForm = useCallback(() => {
    try {
      if (horse) {
        // Editing existing horse - safely merge with defaults
        const initialData = {
          ...DEFAULT_HORSE_FORM,
          name: horse.name || '',
          tag_number: horse.tag_number || '',
          age: horse.age || '',
          gender: horse.gender || '',
          color: horse.color || '',
          hma: horse.hma || '',
          height: horse.height || '',
          disposition: horse.disposition || '',
          photo_url: horse.photo_url || '',
          brand_symbols: Array.isArray(horse.brand_symbols) ? horse.brand_symbols : [],
          bonding_level: typeof horse.bonding_level === 'number' ? horse.bonding_level : 0,
          last_care_date: horse.last_care_date || null,
          gentling: horse.gentling && typeof horse.gentling === 'object' ? horse.gentling : DEFAULT_HORSE_FORM.gentling,
          grooming: horse.grooming || '',
          groundwork: horse.groundwork || '',
          daily_feeding: horse.daily_feeding || ''
        };
        setFormData(initialData);
        setPhotoPreview(horse.photo_url ? resolveImageUrl(horse.photo_url) : null);
      } else {
        // Adding new horse
        setFormData({ ...DEFAULT_HORSE_FORM });
        setPhotoPreview(null);
      }
      setIsInitialized(true);
    } catch (error) {
      console.error('Error initializing form:', error);
      setFormData({ ...DEFAULT_HORSE_FORM });
      setPhotoPreview(null);
      setIsInitialized(true);
    }
  }, [horse]);

  useEffect(() => {
    if (open) {
      setIsInitialized(false);
      // Use setTimeout to avoid setState during render
      setTimeout(() => {
        initializeForm();
      }, 0);
    }
  }, [open, initializeForm]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (including HEIC/HEIF)
    if (!isValidImageType(file)) {
      toast.error('Please upload an image file (JPG, PNG, GIF, WEBP, or HEIC)');
      e.target.value = '';
      return;
    }

    try {
      // Process image (convert HEIC if needed, compress if over 8MB)
      toast.info('Processing image...');
      const processedFile = await processImageForUpload(file, 8);
      
      const formDataObj = new FormData();
      formDataObj.append('file', processedFile);

      const response = await api.post('/upload-media', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      let photoUrl = response.data.url;
      if (photoUrl.startsWith('/api/')) {
        photoUrl = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${photoUrl}`;
      }

      setFormData({ ...formData, photo_url: photoUrl });
      setPhotoPreview(photoUrl);
      toast.success(`Photo uploaded! (${formatFileSize(processedFile.size)})`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload photo');
    }
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.name.trim()) {
      alert('Please enter a name for the horse');
      return;
    }

    onSave(formData);
  };

  // Don't render content until initialized to prevent blank screen
  if (!isInitialized && open) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl">
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: '#6FCFD3', borderTopColor: 'transparent' }}></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {horse ? `Edit ${horse.name || 'Horse'}` : 'Add New Horse'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                placeholder="Enter horse name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Tag Number</label>
              <input
                type="text"
                value={formData.tag_number}
                onChange={(e) => setFormData({ ...formData, tag_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                placeholder="e.g., #1234"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Age</label>
              <input
                type="text"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                placeholder="e.g., 5 years"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Gender</label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              >
                <option value="">Select gender</option>
                <option value="Mare">Mare</option>
                <option value="Stallion">Stallion</option>
                <option value="Gelding">Gelding</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Color</label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                placeholder="e.g., Bay, Chestnut"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Height</label>
              <input
                type="text"
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                placeholder="14.2 hands"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">HMA</label>
              <input
                type="text"
                value={formData.hma}
                onChange={(e) => setFormData({ ...formData, hma: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                placeholder="McCullough Peaks"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Disposition</label>
              <input
                type="text"
                value={formData.disposition}
                onChange={(e) => setFormData({ ...formData, disposition: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                placeholder="Gentle, Spirited, etc."
              />
            </div>
          </div>

          {/* Horse Photo */}
          <div>
            <label className="block text-sm font-medium mb-2">Horse Photo</label>
            <input
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              onChange={handleImageUpload}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
            />
            <p className="text-xs text-gray-500 mt-1">Supports iPhone photos (HEIC). Auto-compresses to 8MB.</p>

            {/* Image Preview */}
            {photoPreview && (
              <div className="mt-3">
                <p className="text-xs font-medium mb-2">Preview:</p>
                <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-300">
                  <img
                    src={photoPreview}
                    alt="Horse preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, photo_url: '' });
                      setPhotoPreview(null);
                    }}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Brand Symbols */}
          <BrandSymbolSelector
            selectedSymbols={formData.brand_symbols}
            onChange={(symbols) => setFormData({ ...formData, brand_symbols: symbols })}
          />

          {/* Gentling Checklist */}
          <div>
            <h3 className="text-base font-semibold mb-2">Training & Care Notes</h3>
            <GentlingChecklist
              gentling={formData.gentling}
              onChange={(updatedGentling) => setFormData({ ...formData, gentling: updatedGentling })}
            />
          </div>

          {/* Text Notes */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Grooming Notes</label>
              <textarea
                value={formData.grooming}
                onChange={(e) => setFormData({ ...formData, grooming: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                rows="3"
                placeholder="Notes about grooming routine..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Groundwork Notes</label>
              <textarea
                value={formData.groundwork}
                onChange={(e) => setFormData({ ...formData, groundwork: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                rows="3"
                placeholder="Notes about groundwork training..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Daily Feeding Notes</label>
              <textarea
                value={formData.daily_feeding}
                onChange={(e) => setFormData({ ...formData, daily_feeding: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                rows="3"
                placeholder="Notes about feeding schedule..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2 text-white rounded-lg transition-all"
              style={{ backgroundColor: '#6FCFD3' }}
            >
              {horse ? 'Save Changes' : 'Add Horse'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditHorseDialog;
